// src/services/locationService.js
import { repo } from "../dbservice/repo";
import { LocationModel } from "../models/LocationModel";

class LocationService {
  constructor() {
    this.collectionName = "locations";
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[LocationService] Failed to parse userData", e);
      return null;
    }
  }

  /**
   * Create a new location with automatic audit stamping.
   */
  async create(data, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || data.locationCode || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      locationCode: data.locationCode || targetId,
      companyId: data.companyId || user?.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  /**
   * Fetch locations scoped to the current user's company.
   */
  async fetchLocations({ companyId = "", status = "" } = {}) {
    const user = this.currentUser;
    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId;
    }

    const filters = [];
    if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }
    if (status) {
      filters.push({ field: "status", value: status });
    }

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[LocationService] Failed to fetch locations:", error);
      return [];
    }
  }

  /**
   * Fetch a single location by ID.
   */
  async fetchById(id) {
    if (!id) {
      console.error("[LocationService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[LocationService] Failed to fetch location ${id}:`, error);
      return null;
    }
  }

  /**
   * Update an existing location — preserves historical creation metadata.
   */
  async update(id, data, currentUser = {}) {
    if (!id) throw new Error("Location ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  /**
   * Core persistence orchestrator with read-before-write metadata preservation.
   */
  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Location ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const instance = new LocationModel(
      { ...finalData, id: targetId },
      { isCreation }
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid location payload: locationCode and locationName are required."
      );
    }

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }

  /**
   * Delete a location record.
   */
  async delete(id) {
    if (!id) throw new Error("Location ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[LocationService] Failed to delete location ${id}:`, error);
      throw error;
    }
  }
}

export const locationService = new LocationService();